import { supabase } from './supabase';
import { GatewayData, SupabaseDtuRequest } from '../types';

// Local metrics computation copied/adapted from Dashboard.computeGatewayMetricsAll
function computeGatewayMetricsAll(gateways: GatewayData[]): GatewayData[] {
  const now = Date.now();

  return gateways.map((g) => {
    const nodes = Array.isArray(g.nodes) ? g.nodes : [];
    const nodes_status = Array.isArray(g.nodes_status) ? g.nodes_status : [];

    let normalCount = 0;
    let alertCount = 0;
    let brokenCount = 0;

    for (let i = 0; i < 10; i++) {
      const val = nodes[i] ?? 0;
      const status = nodes_status[i] ?? 0;
      if (status === 0) {
        brokenCount += 1;
      } else {
        if (val !== 1) alertCount += 1;
        else normalCount += 1;
      }
    }

    let dtuStatus: 'normal' | 'alert' | 'malfunction' | 'disconnected' = 'normal';
    if (brokenCount > 0) dtuStatus = 'malfunction';
    else if (alertCount > 0) dtuStatus = 'alert';
    else dtuStatus = 'normal';

    return {
      ...g,
      normalCount,
      alertCount,
      brokenCount,
      dtuStatus,
    } as GatewayData;
  });
}

// DTU order mapping: actual installation order -> logical order
const DTU_ORDER_MAPPING: { [key: string]: string } = {
  // STR-NB
  'DTU-010': 'STR-NB-002',
  'DTU-004': 'STR-NB-001',
  'DTU-011': 'STR-NB-003',
  'DTU-008': 'STR-NB-004',
  'DTU-005': 'STR-NB-005',
  'TEST-DTU-038': 'STR-NB-006', // DTU-012  -> TEST-DTU-038
  'DTU-013': 'STR-NB-007',
  'DTU-009': 'STR-NB-008',
  'DTU-006': 'STR-NB-009',
  'TEST-DTU-037': 'STR-NB-010', // DTU-007  -> TEST-DTU-037
  'TEST-DTU-035': 'STR-NB-011', // DTU-003 -> TEST-DTU-035
  'DTU-001': 'STR-NB-012',
  'DTU-002': 'STR-NB-013',
  'TEST-DTU-032': 'STR-NB-014',
  'REPLACE-02'  : 'STR-NB-015',
  'REPLACE-01'  : 'STR-NB-016',
  'TEST-DTU-033': 'STR-NB-017',
  'TEST-DTU-013': 'STR-NB-018',
  'TEST-DTU-036': 'STR-NB-019', // TEST-DTU-019  -> TEST-DTU-036
  'TEST-DTU-034': 'STR-NB-020',
  'TEST-DTU-020': 'STR-NB-021',
  'TEST-DTU-018': 'STR-NB-022',
  'TEST-DTU-016': 'STR-NB-023',
  'TEST-DTU-015': 'STR-NB-024',
  'TEST-DTU-014': 'STR-NB-025',
  'TEST-DTU-021': 'STR-NB-026',
  'TEST-DTU-022': 'STR-NB-027',
  
  
  // STR-CM (UPPER)
  'TEST-DTU-023': 'STR-CM-001',
  'TEST-DTU-024': 'STR-CM-002',
  'TEST-DTU-025': 'STR-CM-003',
  'TEST-DTU-026': 'STR-CM-004',
  'TEST-DTU-027': 'STR-CM-005',
  
  // STR-CM (LOWER)
  'TEST-DTU-017': 'STR-CM-006',
  'TEST-DTU-028': 'STR-CM-007',
  'TEST-DTU-029': 'STR-CM-008',
  'TEST-DTU-030': 'STR-CM-009',
  'TEST-DTU-031': 'STR-CM-010',
  
  // TESTING
  // 'SPARE-01': 'SPARE-01',
  
};

// Reverse DTU order mapping: logical order -> actual installation order
const REVERSE_DTU_ORDER_MAPPING: { [key: string]: string } = {
  // STR-NB
  'STR-NB-002' : 'DTU-010',
  'STR-NB-001' : 'DTU-004',
  'STR-NB-003' : 'DTU-011',
  'STR-NB-004' : 'DTU-008',
  'STR-NB-005' : 'DTU-005',
  'STR-NB-006' : 'TEST-DTU-038', // DTU-012 -> TEST-DTU-038
  'STR-NB-007' : 'DTU-013',
  'STR-NB-008' : 'DTU-009',
  'STR-NB-009' : 'DTU-006',
  'STR-NB-010' : 'TEST-DTU-037', // DTU-007 -> TEST-DTU-037
  'STR-NB-011' : 'TEST-DTU-035', // DTU-003 -> TEST-DTU-035
  'STR-NB-012' : 'DTU-001',
  'STR-NB-013' : 'DTU-002',
  'STR-NB-014' : 'TEST-DTU-032',
  'STR-NB-015' : 'REPLACE-02',
  'STR-NB-016' : 'REPLACE-01',
  'STR-NB-017' : 'TEST-DTU-033',
  'STR-NB-018' : 'TEST-DTU-013',
  'STR-NB-019' : 'TEST-DTU-036', // TEST-DTU-019 -> TEST-DTU-036
  'STR-NB-020' : 'TEST-DTU-034',
  'STR-NB-021' : 'TEST-DTU-020',
  'STR-NB-022' : 'TEST-DTU-018',
  'STR-NB-023' : 'TEST-DTU-016',
  'STR-NB-024' : 'TEST-DTU-015',
  'STR-NB-025' : 'TEST-DTU-014',
  'STR-NB-026' : 'TEST-DTU-021',
  'STR-NB-027' : 'TEST-DTU-022',

  // STR-CM (UPPER)
  'STR-CM-001' : 'TEST-DTU-023',
  'STR-CM-002' : 'TEST-DTU-024',
  'STR-CM-003' : 'TEST-DTU-025',
  'STR-CM-004' : 'TEST-DTU-026',
  'STR-CM-005' : 'TEST-DTU-027',
  
  // STR-CM (LOWER)
  'STR-CM-006' : 'TEST-DTU-017',
  'STR-CM-007' : 'TEST-DTU-028',
  'STR-CM-008' : 'TEST-DTU-029',
  'STR-CM-009' : 'TEST-DTU-030',
  'STR-CM-010' : 'TEST-DTU-031',
  
  // TESTING
  // 'SPARE-01': 'SPARE-01',
};

// Slave ID mapping for DTUs with incorrect installation order
const SLAVE_ID_MAPPING: { [dtuAlias: string]: { [actualSlaveId: number]: number } } = {
  // DTU-001 and DTU-002: reverse order (1-10 to 10-1)
  'DTU-001': {
    1: 10, 2: 9, 3: 8, 4: 7, 5: 6,
    6: 5, 7: 4, 8: 3, 9: 2, 10: 1
  },
  'DTU-002': {
    1: 10, 2: 9, 3: 8, 4: 7, 5: 6,
    6: 5, 7: 4, 8: 3, 9: 2, 10: 1
  },
  // DTU-006: 5,7,9,6,4,1,10,8,2,3 -> 1,2,3,4,5,6,7,8,9,10
  'DTU-006': {
    5: 1, 7: 2, 9: 3, 6: 4, 4: 5,
    1: 6, 10: 7, 8: 8, 2: 9, 3: 10
  },
  // // DTU-011: 1,2,4,6,7,3,10,8,9,5 -> 1,2,3,4,5,6,7,8,9,10
  // 'DTU-011': {
  //   1: 1, 2: 2, 4: 3, 6: 4, 7: 5,
  //   3: 6, 10: 7, 8: 8, 9: 9, 5: 10
  // }
};

// Helper function to map alias according to installation order
function mapDtuAlias(alias: string): string {
  return DTU_ORDER_MAPPING[alias] || alias;
}

// Helper function to reverse map alias from logical order to actual installation order
function reverseMapDtuAlias(alias: string): string {
  return REVERSE_DTU_ORDER_MAPPING[alias] || alias;
}

// Helper function to map slave ID according to installation order
function mapSlaveId(dtuAlias: string, actualSlaveId: number): number {
  const mapping = SLAVE_ID_MAPPING[dtuAlias];
  return mapping ? (mapping[actualSlaveId] || actualSlaveId) : actualSlaveId;
}



export async function fetchGatewayDataFromSupabase(): Promise<GatewayData[]> {
  if (!supabase) {
    throw new Error('Supabase client not initialized. Please check your Supabase configuration.');
  }

  try {
    const now = new Date();
    const timeThreshold = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000);
    // Get all DTU aliases from the reverse mapping (actual installation order)
    const dtuAliases = Object.keys(DTU_ORDER_MAPPING).sort((a, b) => b.localeCompare(a));
    // Process each gateway's combined data
    const processedData: GatewayData[] = [];

    // Call the RPC function to get aggregated data for all aliases
    const { data: aggregatedData, error } = await supabase.rpc('get_gateway_data', {
      time_threshold: timeThreshold.toISOString(),
      aliases: dtuAliases
    });

    if (error) {
      console.error('Error fetching aggregated data:', error);
    } else if (aggregatedData && aggregatedData.length > 0) {
      // Process each aggregated row
      for (const item of aggregatedData) {
        // Skip if no device data
        if (!item.devices || item.devices.length === 0) {
          continue;
        }

        // Get location data (may be null)
        const loc_lat = item.loc_lat ?? 22.373989;
        const loc_lng = item.loc_lng ?? 114.193266;

        // Get online timestamp
        let DTU_online_time = new Date(0);
        if (item.dtu_online_time) {
          const timestamp = new Date(item.dtu_online_time);
          DTU_online_time = new Date(timestamp.getTime() + timestamp.getTimezoneOffset() * 60000);
        }

        // The devices array already contains the latest record per slave_id
        const latestRecordsBySlaveId = new Map<number, SupabaseDtuRequest>();
        item.devices.forEach((record: any) => {
          latestRecordsBySlaveId.set(record.slave_id, record);
        });

        // Build nodes arrays
        const nodes: number[] = new Array(10).fill(0);
        const nodes_status: number[] = new Array(10).fill(0);
        const sensor_online_time: Date[] = new Array(10).fill(new Date(0));
        latestRecordsBySlaveId.forEach((record) => {
          const mappedSlaveId = mapSlaveId(item.alias, record.slave_id);
          const arrayIndex = mappedSlaveId - 1; // Convert to 0-based index
          if (arrayIndex >= 0 && arrayIndex < 10) {
            nodes[arrayIndex] = record.water ? 1 : 0;
            const timestamp = new Date(record.created_at);
            const utc_timestamp = new Date(timestamp.getTime() + timestamp.getTimezoneOffset() * 60000);
            sensor_online_time[arrayIndex] = utc_timestamp;
            if (DTU_online_time.getTime() - utc_timestamp.getTime() <= 0 && record.RSSI != 0)
              nodes_status[arrayIndex] = 1;
            else
              nodes_status[arrayIndex] = 0;
          }
        });

        // Map to logical alias
        const mappedAlias = mapDtuAlias(item.alias);
        const gatewayData = {
          _id: `${mappedAlias}-latest`,
          gateway_id: mappedAlias,
          time: DTU_online_time.toISOString(),
          timestamp: DTU_online_time.getTime(),
          nodes: nodes,
          nodes_status: nodes_status,
          loc_x: loc_lng,
          loc_y: loc_lat,
          alias: mappedAlias,
          lat: loc_lat,
          lng: loc_lng,
          sensor_online_time: sensor_online_time,
          real_time: DTU_online_time,
        } as GatewayData;
        processedData.push(gatewayData);
      }
    }
    return processedData;

  } catch (error) {
    console.error('❌ fetchGatewayDataFromSupabase error:', error);
    throw error;
  }
}

export async function fetchMultipleDtuHistoryFromSupabase(
  dtuAliases: string[],          // logical aliases, e.g. ["A1", "B2"]
  days: number = 28
): Promise<GatewayData[]> {
  if (!supabase) {
    throw new Error('Supabase client not initialized.');
  }

  if (dtuAliases.length === 0) return [];

  // Reverse-map all logical aliases to the actual stored aliases
  const reverseMappedAliases = dtuAliases.map(alias => reverseMapDtuAlias(alias));

  const now = new Date();
  const timeThreshold = new Date(now.getTime() - days * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000);

  const PAGE_SIZE = 1000;

  // Generic paginated fetch helper
  async function fetchAllFromTable(
    table: string,
    selectCols: string = '*',
    applyTimeFilter: boolean = true,
    orderByCreatedAt: boolean = true
  ): Promise<any[]> {
    let accumulated: any[] = [];
    let start = 0;

    while (true) {
      let query: any = supabase
        .from(table)
        .select(selectCols)
        .in('alias', reverseMappedAliases);

      if (applyTimeFilter) query = query.gte('created_at', timeThreshold.toISOString());
      if (orderByCreatedAt) query = query.order('created_at', { ascending: false });

      query = query.range(start, start + PAGE_SIZE - 1);

      const { data, error } = await query;
      if (error) throw error;

      if (!data || data.length === 0) break;

      accumulated = accumulated.concat(data);

      if (data.length < PAGE_SIZE) break;

      start += PAGE_SIZE;
    }

    return accumulated;
  }

  // Fetch paginated data for large tables; static info fetched once (no date filter)
  const [
    onlineData,
    deviceData,
    locationData,
    locationStaticData
  ] = await Promise.all([
    fetchAllFromTable('DTU_ONLINE', '*', true, true),
    fetchAllFromTable('DTU_reply', '*', true, true),
    fetchAllFromTable('DTU_LOC', '*', true, true),
    fetchAllFromTable('DTU_INFO_STATIC', 'alias, loc_lat, loc_lng', false, false)
  ]);

  // Create lookup maps for static location (latest reference)
  const staticLocationMap = new Map<string, { loc_lat: number; loc_lng: number }>();
  locationStaticData?.forEach(row => {
    staticLocationMap.set(row.alias, { loc_lat: row.loc_lat, loc_lng: row.loc_lng });
  });

  // Helper to calculate distance in meters (Haversine)
  const calculateDistance = (
    lat1: number, lng1: number,
    lat2: number, lng2: number
  ): number => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) ** 2 +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const historyData: GatewayData[] = [];

  // Process each DTU separately
  for (const logicalAlias of dtuAliases) {
    const actualAlias = reverseMapDtuAlias(logicalAlias);

    // Filter data for this DTU
    const dtuOnline = (onlineData ?? []).filter(r => r.alias === actualAlias);
    const dtuDevices = (deviceData ?? []).filter(r => r.alias === actualAlias);
    const dtuLocations = (locationData ?? []).filter(r => r.alias === actualAlias);

    if (dtuOnline.length === 0 || dtuDevices.length === 0) continue;

    const latestStatic = staticLocationMap.get(actualAlias);
    if (!latestStatic) continue; // no reference location

    // Convert online timestamps to UTC-adjusted Date objects and sort oldest → newest
    const sortedOnlineTimes = dtuOnline
      .map(o => {
        const t = new Date(o.created_at);
        return new Date(t.getTime() + t.getTimezoneOffset() * 60000);
      })
      .sort((a, b) => a.getTime() - b.getTime());

    // Process each online session
    sortedOnlineTimes.forEach((onlineTime, idx) => {
      const nextOnlineTime = idx < sortedOnlineTimes.length - 1
        ? sortedOnlineTimes[idx + 1]
        : new Date(); // current time as upper bound for the last session

      // Find location near this online event (±2 seconds)
      const sessionLocation = dtuLocations.find(loc => {
        const locTime = new Date(loc.created_at);
        const adjusted = new Date(locTime.getTime() + locTime.getTimezoneOffset() * 60000);
        return Math.abs(adjusted.getTime() - onlineTime.getTime()) <= 2000;
      });

      if (!sessionLocation) return; // no location → skip

      const distance = calculateDistance(
        latestStatic.loc_lat,
        latestStatic.loc_lng,
        sessionLocation.loc_lat,
        sessionLocation.loc_lng
      );

      if (distance > 20) return; // moved too far → invalid session

      // Sensor records that fall into this session
      const sessionSensors = dtuDevices.filter(rec => {
        const sensorTime = new Date(rec.created_at);
        const adjusted = new Date(sensorTime.getTime() + sensorTime.getTimezoneOffset() * 60000);
        return adjusted.getTime() >= onlineTime.getTime() &&
               adjusted.getTime() < nextOnlineTime.getTime();
      });

      if (sessionSensors.length === 0) return;

      // Latest record per slave_id in this session
      const latestBySlave = new Map<number, any>();
      sessionSensors.forEach(rec => {
        const existing = latestBySlave.get(rec.slave_id);
        if (!existing || new Date(rec.created_at) > new Date(existing.created_at)) {
          latestBySlave.set(rec.slave_id, rec);
        }
      });

      const nodes = new Array(10).fill(0);
      const nodes_status = new Array(10).fill(0);
      const sensor_online_time = new Array(10).fill(new Date(0));

      Array.from(latestBySlave.values()).forEach(rec => {
        const mappedSlaveId = mapSlaveId(actualAlias, rec.slave_id);
        const index = mappedSlaveId - 1;
        if (index >= 0 && index < 10) {
          nodes[index] = rec.water ? 1 : 0;
          const ts = new Date(rec.created_at);
          const adjusted = new Date(ts.getTime() + ts.getTimezoneOffset() * 60000);
          sensor_online_time[index] = adjusted;
          nodes_status[index] = rec.RSSI !== 0 ? 1 : 0;
        }
      });

      const gatewayData = {
        _id: `${logicalAlias}-${onlineTime.toISOString()}`,
        gateway_id: logicalAlias,
        time: onlineTime.toISOString(),
        timestamp: onlineTime.getTime(),
        nodes,
        nodes_status,
        loc_x: latestStatic.loc_lng,
        loc_y: latestStatic.loc_lat,
        alias: logicalAlias,
        lat: latestStatic.loc_lat || 22.373989,
        lng: latestStatic.loc_lng || 114.193266,
        sensor_online_time,
        real_time: onlineTime,
      } as GatewayData;

      historyData.push(gatewayData);
    });
  }
  
  historyData.reverse();
  return historyData;
}

// Fetch export data for UI export action. Two modes supported:
// - { type: 'days', days } -> fetch history for each selected DTU using fetchDtuHistoryFromSupabase
// - { type: 'latest' } -> use provided currentGatewayData (or fetch latest via existing function) and return latest entries
export async function fetchExportData(
  selectedDtus: string[] = [],
  mode: { type: 'days'; days: number } | { type: 'latest' },
  currentGatewayData?: GatewayData[]
): Promise<GatewayData[]> {
  // If no DTUs are selected, default to all logical DTU aliases
  const dtusToProcess = (selectedDtus && selectedDtus.length > 0)
    ? selectedDtus
    : Object.keys(REVERSE_DTU_ORDER_MAPPING).map(mapDtuAlias);
  
  const dtusToProcessSorted = dtusToProcess.sort((a, b) => b.localeCompare(a));

  if (mode.type === 'days') {
    // Fetch historical data for each DTU and concatenate
    const history = await fetchMultipleDtuHistoryFromSupabase(dtusToProcessSorted, Math.max(1, Math.min(28, mode.days)));
    // also compute metrics across any remaining entries just in case
    return computeGatewayMetricsAll(history);
  } else {
    // latest: return one latest record per DTU from the provided currentGatewayData
    const source = currentGatewayData || [];
    // Ensure we have at most one entry per gateway_id (keep latest by timestamp)
    const latestByGateway = new Map<string, GatewayData>();
    source.forEach((g) => {
      const key = g.gateway_id || g.alias || g._id;
      const existing = latestByGateway.get(key);
      if (!existing || (g.timestamp || 0) > (existing.timestamp || 0)) {
        latestByGateway.set(key, g);
      }
    });

    let out = Array.from(latestByGateway.values());

    // If specific DTUs selected, filter
    if (selectedDtus && selectedDtus.length > 0) {
      out = out.filter(g => selectedDtus.includes(g.gateway_id) || selectedDtus.includes(g.alias));
    }

    // Default sort by alias ascending for stable export
    out.sort((a, b) => (a.alias || a.gateway_id || '').localeCompare(b.alias || b.gateway_id || ''));
    return out;
  }
}

// Convert GatewayData[] to CSV string according to required column layout.
export function convertGatewayDataToCSV(gateways: GatewayData[]): string {
  const headerCols = [
    'DTU name',
    'Time',
    'Latitude',
    'Longitude',
    'DTU status',
    'Normal Sensor Counts',
    'Malfunction Sensor Counts',
    'Alert Sensor Counts'
  ];

  // Add sensor columns: sensor1, sensor1 status, ..., sensor10, sensor10 status
  for (let i = 1; i <= 10; i++) {
    headerCols.push(`Sensor${i} Status`);
  }

  const rows: string[] = [];
  rows.push(headerCols.join(','));

  const mapStatus = (nodes: number[] | undefined, nodes_status: number[] | undefined, idx: number) => {
    const nodeVal = nodes && nodes.length > idx ? nodes[idx] : 0;
    const nodeStatusFlag = nodes_status && nodes_status.length > idx ? nodes_status[idx] : 0;

    // Interpret status: nodeStatusFlag === 0 => malfunction (no recent data or RSSI 0)
    if (nodeStatusFlag === 0) return 'Malfunction';
    // If node indicates water (=1) treat as low-level alert
    if (nodeVal !== 1) return 'Low-level';
    return 'Normal';
  };

  gateways.forEach((g) => {
    const alias = g.alias || g.gateway_id || g._id || '';
    const time = g.real_time ? new Date(g.real_time.getTime() + 28800000).toISOString() : (g.time || '');
    const lat = (g.lat !== undefined) ? String(g.lat) : '';
    const lng = (g.lng !== undefined) ? String(g.lng) : '';
    const dtustatus = g.dtuStatus || '';
    const normalCount = (g.normalCount !== undefined) ? String(g.normalCount) : '';
    const malfunctionCount = (g.brokenCount !== undefined) ? String(g.brokenCount) : '';
    const alertCount = (g.alertCount !== undefined) ? String(g.alertCount) : '';

    const baseCols = [alias, time, lat, lng, dtustatus, normalCount, malfunctionCount, alertCount];

    const sensorCols: string[] = [];
    for (let i = 0; i < 10; i++) {
      const sensorStatus = mapStatus(g.nodes, g.nodes_status, i);
      sensorCols.push(sensorStatus);
    }

    // Escape commas in alias if any by wrapping in double quotes
    const row = [...baseCols, ...sensorCols].map((c) => {
      if (typeof c === 'string' && c.includes(',')) return `"${c.replace(/"/g, '""')}"`;
      return c;
    }).join(',');

    rows.push(row);
  });

  return rows.join('\n');
}

// Cache for grouped DTU history (index-based groups across DTUs)
export let cachedAllDtuHistoryGrouped: GatewayData[][] | null = null;

export function clearCachedAllDtuHistoryGrouped() {
  cachedAllDtuHistoryGrouped = null;
}

// Fetch 28-day history for all DTUs, group by index across DTUs, compute metrics for each group,
// and cache the result to avoid redundant queries.
export async function fetchAllDtuHistoryGrouped(days: number = 28): Promise<GatewayData[][]> {
  if (cachedAllDtuHistoryGrouped) return cachedAllDtuHistoryGrouped;

  // Use logical aliases as keys from the reverse mapping
  const dtuAliases = Object.keys(REVERSE_DTU_ORDER_MAPPING);

  const allHistoryFlat: GatewayData[] = await fetchMultipleDtuHistoryFromSupabase(
        dtuAliases,
        days
      );

      // Group by gatewayId (logical alias) and sort each group by timestamp descending
      const groupedMap = new Map<string, GatewayData[]>();

      for (const record of allHistoryFlat) {
        const alias = record.gateway_id; // logical alias
        if (!groupedMap.has(alias)) {
          groupedMap.set(alias, []);
        }
        groupedMap.get(alias)!.push(record);
      }

      // Sort each group by timestamp descending (newest first)
      groupedMap.forEach((records) => {
        records.sort((a, b) => b.timestamp - a.timestamp);
      });

      // Build final array in the exact order of logicalDtuAliases
      const perDtuHistories: GatewayData[][] = dtuAliases.map(alias => {
        return groupedMap.get(alias) || [];
      });

  // Find maximum number of entries among all DTUs
  const maxLen = perDtuHistories.reduce((m, arr) => Math.max(m, arr.length), 0);

  const grouped: GatewayData[][] = [];

  // For each index i, collect the i-th most recent record from each DTU (if present)
  for (let i = 0; i < maxLen; i++) {
    const group: GatewayData[] = [];
    for (const hist of perDtuHistories) {
      if (hist && hist.length > i && hist[i]) {
        group.push(hist[i]);
      }
    }

    // Compute metrics for this group (adds counts/status)
    const computed = computeGatewayMetricsAll(group);
    grouped.push(computed);
  }

  // Cache the grouped result
  cachedAllDtuHistoryGrouped = grouped;
  return grouped;
}
